// Shared deduction aging logic: age bases, 30/60/90 buckets, stage scopes, and the
// aging summary computed for the AgingPanel. Pure functions only — no component imports.
import { differenceInDays, parseISO } from "date-fns"

import { DeductionListJson } from "@/api/deduction"
import { CurrencyFields, to_reporting_amount } from "@/utils/currency"

/** Which date anchors a deduction's "age". `until_dispute_expiration` counts days remaining. */
export type AgeBasis = "invoice_date" | "check_date" | "since_disputed" | "in_current_stage" | "until_dispute_expiration"

export const AGE_BASIS_LABELS: Record<AgeBasis, string> = {
  invoice_date: "Invoice date",
  check_date: "Check date",
  since_disputed: "Since disputed",
  in_current_stage: "Time in stage",
  until_dispute_expiration: "Until dispute expires",
}

/** Compact parenthetical for Days Old badges / column headers. */
export const DAYS_OLD_BASIS_SHORT_LABELS: Record<AgeBasis, string> = {
  invoice_date: "invoice",
  check_date: "check",
  since_disputed: "disputed",
  in_current_stage: "stage",
  until_dispute_expiration: "expiry",
}

/**
 * Days Old column / detail field always follow the Aging panel's age basis
 * (including "until dispute expires"). The global invoice/check toggle only
 * filters the date range — it does not override this column.
 */
export function resolve_days_old_basis(aging_basis: AgeBasis): AgeBasis {
  return aging_basis
}

/** Column header title for the current basis. */
export function days_old_column_title(basis: AgeBasis): string {
  if (basis === "until_dispute_expiration") return "Days Left (expiry)"
  return `Days Old (${DAYS_OLD_BASIS_SHORT_LABELS[basis]})`
}

/** Table badge text: "311 days (check)" / "53 days left (expiry)" / "Expired". */
export function format_days_old_badge(days: number, basis: AgeBasis): string {
  if (basis === "until_dispute_expiration") {
    if (days < 0) return "Expired"
    return `${days} days left (${DAYS_OLD_BASIS_SHORT_LABELS[basis]})`
  }
  return `${days} days (${DAYS_OLD_BASIS_SHORT_LABELS[basis]})`
}

/** Detail / tooltip text. */
export function format_days_old_detail(days: number, basis: AgeBasis): string {
  switch (basis) {
    case "invoice_date":
      return `${days} days (since invoice date)`
    case "check_date":
      return `${days} days (since check date)`
    case "since_disputed":
      return `${days} days (since disputed)`
    case "in_current_stage":
      return `${days} days (in current stage)`
    case "until_dispute_expiration":
      if (days < 0) return `Expired ${Math.abs(days)} days ago`
      return `${days} days left (until dispute expires)`
  }
}

/**
 * Frontend mirror of the per-source dispute windows in `db/src/deduction/dispute_window.rs`
 * (the source of truth — keep in sync). Only used for messaging and the `?extended` escape
 * hatch; actual deadlines come server-computed as `dispute_deadline`.
 */
export const DISPUTE_WINDOW_DAYS: Record<string, number> = {
  unfi: 365,
  // SV Harbor is UNFI's supplier portal: UNFI's charges, UNFI's one-year window.
  svharbor: 365,
  kehe: 180,
  cvs: 45,
  amazon: 60,
  ahold: 750,
}

/** Structural subset of DeductionListJson / DeductionDetailJson / TransactionJson used for aging. */
export interface AgeFields {
  invoice_date: string
  check_date?: string | null
  dispute_created_at?: string | null
  status_changed_at?: string | null
  dispute_deadline?: string | null
}

/**
 * Days elapsed for the given basis, or null when the anchor date is missing.
 * `until_dispute_expiration` returns days REMAINING (negative = expired).
 * The `check_date` basis falls back to `invoice_date` when payment date is unknown.
 */
export function compute_age_days(row: AgeFields, basis: AgeBasis, now: Date = new Date()): number | null {
  switch (basis) {
    case "invoice_date":
      return differenceInDays(now, parseISO(row.invoice_date))
    case "check_date": {
      const anchor = row.check_date || row.invoice_date
      return anchor ? differenceInDays(now, parseISO(anchor)) : null
    }
    case "since_disputed":
      return row.dispute_created_at ? differenceInDays(now, parseISO(row.dispute_created_at)) : null
    case "in_current_stage":
      return row.status_changed_at ? differenceInDays(now, parseISO(row.status_changed_at)) : null
    case "until_dispute_expiration":
      return row.dispute_deadline ? differenceInDays(parseISO(row.dispute_deadline), now) : null
  }
}

export interface AgeBucket {
  key: string
  label: string
  /** Inclusive lower bound in days; null = unbounded below. */
  min: number | null
  /** Inclusive upper bound in days; null = unbounded above. */
  max: number | null
  /** The actionable-urgency bucket gets visual emphasis (red). */
  emphasized?: boolean
}

export const AGE_BUCKETS: AgeBucket[] = [
  { key: "0_30", label: "0–30 days", min: 0, max: 30 },
  { key: "31_60", label: "31–60 days", min: 31, max: 60 },
  { key: "61_90", label: "61–90 days", min: 61, max: 90 },
  // Deliberately NOT emphasized: a big red 90+ pile reads as "the tool isn't working".
  // Urgency emphasis lives on the about-to-expire expiry buckets instead.
  { key: "90_plus", label: "90+ days", min: 91, max: null },
]

/**
 * Buckets for the `until_dispute_expiration` basis (days remaining, negative = expired).
 * Only "≤30 days left" is emphasized — that's the actionable one; Expired is already lost
 * and painting it red just looks bad.
 */
export const EXPIRY_BUCKETS: AgeBucket[] = [
  { key: "expired", label: "Expired", min: null, max: -1 },
  { key: "expiring_30", label: "≤30 days left", min: 0, max: 30, emphasized: true },
  { key: "expiring_31_90", label: "31–90 days left", min: 31, max: 90 },
  { key: "expiring_90_plus", label: "90+ days left", min: 91, max: null },
]

export function buckets_for_basis(basis: AgeBasis): AgeBucket[] {
  return basis === "until_dispute_expiration" ? EXPIRY_BUCKETS : AGE_BUCKETS
}

function bucket_contains(bucket: Pick<AgeBucket, "min" | "max">, days: number): boolean {
  if (bucket.min != null && days < bucket.min) return false
  if (bucket.max != null && days > bucket.max) return false
  return true
}

export function bucket_for_days(days: number, basis: AgeBasis): AgeBucket | null {
  return buckets_for_basis(basis).find(b => bucket_contains(b, days)) ?? null
}

/** Column-filter value set by the AgingPanel on the `days_old` column. */
export interface AgeBucketFilterValue {
  basis: AgeBasis
  min: number | null
  max: number | null
}

/** Guard for persisted/stale filter values: the days_old filterFn falls back to text matching otherwise. */
export function is_age_bucket_filter_value(value: unknown): value is AgeBucketFilterValue {
  return typeof value === "object" && value !== null && "basis" in value
}

/** Core of the `days_old` column filterFn: does the row's age (for the value's basis) fall in range? */
export function age_bucket_filter(row: AgeFields, value: AgeBucketFilterValue, now: Date = new Date()): boolean {
  const days = compute_age_days(row, value.basis, now)
  if (days == null) return false
  return bucket_contains(value, days)
}

export type StagePresetKey = "open_pending_validation" | "open_pending_backup" | "open_pending_dispute" | "all_open" | "all"

/** Non-terminal stages: everything except won/lost/archived/expensable/validated. */
export const OPEN_STAGES = ["new", "review", "pending", "task_assigned", "disputable", "backup_requested", "backup_received", "disputed"]

/**
 * Every stage, open ones first. The aging panel's stage checkbox list renders from this
 * complete list (NOT the org-gated STATUS_VALUES, which hides e.g. `pending` for most orgs —
 * an invisible option can end up stuck in a custom scope with no way to uncheck it).
 */
export const ALL_STAGES = [...OPEN_STAGES, "validated", "won", "expensable", "lost", "archived"]

/** Quick stage-scope filters for the AgingPanel. `stages: null` = no stage restriction. */
export const STAGE_PRESETS: Record<StagePresetKey, { label: string; stages: string[] | null }> = {
  all_open: { label: "All open", stages: OPEN_STAGES },
  open_pending_validation: { label: "Open – pending validation", stages: ["new", "review", "pending", "task_assigned"] },
  open_pending_backup: { label: "Open – pending backup", stages: ["backup_requested"] },
  open_pending_dispute: { label: "Open – pending dispute", stages: ["disputable", "backup_received"] },
  all: { label: "All stages", stages: null },
}

export function stage_in_scope(status_value: string, scope: string[] | null): boolean {
  return scope == null || scope.includes(status_value)
}

/**
 * Dollar amount a row contributes to aging totals. Mirrors the selection rules of
 * `tallyData` in StatusBreakdown so the aging panel and the stage chart agree.
 */
export function aging_row_amount(
  row: Pick<DeductionListJson, "kind" | "won_amount" | "status_value" | "dispute_amount" | "deduction_amount"> & CurrencyFields
): number {
  // In dollars, so a row paid in another currency adds to the rest (`to_reporting_amount`).
  if (row.won_amount && row.status_value === "won") {
    return to_reporting_amount(row, row.won_amount)
  }
  if (row.kind === "invoice") {
    // Early-pay invoices: list SQL sets deduction_amount from discount_amount; dispute_amount
    // can reflect full invoice and would overcount.
    return to_reporting_amount(row, row.deduction_amount)
  }
  if (row.dispute_amount) {
    return to_reporting_amount(row, row.dispute_amount)
  }
  return to_reporting_amount(row, row.deduction_amount)
}

export interface BucketTally {
  count: number
  amount: number
}

export interface CustomerAgingRow {
  customer_name: string
  total_count: number
  total_amount: number
  buckets: Record<string, BucketTally>
}

export interface AgingSummary {
  total_count: number
  total_amount: number
  buckets: Record<string, BucketTally>
  by_customer: CustomerAgingRow[]
}

function empty_buckets(defs: AgeBucket[]): Record<string, BucketTally> {
  return Object.fromEntries(defs.map(b => [b.key, { count: 0, amount: 0 }]))
}

/**
 * Bucket counts and $ totals (overall and per customer, sorted by $ desc) for rows in the
 * stage scope. Repayments and rows with no age for the basis are skipped.
 */
export function aging_summary(data: DeductionListJson[], basis: AgeBasis, scope: string[] | null, now: Date = new Date()): AgingSummary {
  const bucket_defs = buckets_for_basis(basis)
  const summary: AgingSummary = {
    total_count: 0,
    total_amount: 0,
    buckets: empty_buckets(bucket_defs),
    by_customer: [],
  }
  const customers = new Map<string, CustomerAgingRow>()
  for (const row of data) {
    if (row.kind === "repayment") continue
    if (!stage_in_scope(row.status_value, scope)) continue
    const days = compute_age_days(row, basis, now)
    if (days == null) continue
    const bucket = bucket_for_days(days, basis)
    if (!bucket) continue
    const amount = Math.abs(aging_row_amount(row))
    summary.total_count += 1
    summary.total_amount += amount
    summary.buckets[bucket.key].count += 1
    summary.buckets[bucket.key].amount += amount
    let customer = customers.get(row.customer_name)
    if (!customer) {
      customer = { customer_name: row.customer_name, total_count: 0, total_amount: 0, buckets: empty_buckets(bucket_defs) }
      customers.set(row.customer_name, customer)
    }
    customer.total_count += 1
    customer.total_amount += amount
    customer.buckets[bucket.key].count += 1
    customer.buckets[bucket.key].amount += amount
  }
  summary.by_customer = [...customers.values()].sort((a, b) => b.total_amount - a.total_amount)
  return summary
}

/** Top `n` customer rows by $ with the remainder rolled up into an "Other" row. */
export function limit_customers(rows: CustomerAgingRow[], n: number, basis: AgeBasis): CustomerAgingRow[] {
  if (rows.length <= n) return rows
  const top = rows.slice(0, n)
  const rest = rows.slice(n)
  const other: CustomerAgingRow = {
    customer_name: "Other",
    total_count: 0,
    total_amount: 0,
    buckets: empty_buckets(buckets_for_basis(basis)),
  }
  for (const row of rest) {
    other.total_count += row.total_count
    other.total_amount += row.total_amount
    for (const [key, tally] of Object.entries(row.buckets)) {
      other.buckets[key].count += tally.count
      other.buckets[key].amount += tally.amount
    }
  }
  return [...top, other]
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  const NOW = new Date("2026-07-15T12:00:00Z")

  function row(overrides: Partial<DeductionListJson>): DeductionListJson {
    return {
      kind: "deduction",
      invoice_date: "2026-07-01",
      status_value: "new",
      customer_name: "KeHE",
      deduction_amount: 100,
      ...overrides,
    } as DeductionListJson
  }

  describe("resolve_days_old_basis", () => {
    it("always follows the aging panel basis, including dispute expiry", () => {
      expect(resolve_days_old_basis("check_date")).toBe("check_date")
      expect(resolve_days_old_basis("invoice_date")).toBe("invoice_date")
      expect(resolve_days_old_basis("since_disputed")).toBe("since_disputed")
      expect(resolve_days_old_basis("in_current_stage")).toBe("in_current_stage")
      expect(resolve_days_old_basis("until_dispute_expiration")).toBe("until_dispute_expiration")
    })
  })

  describe("format_days_old_badge / detail", () => {
    it("labels the basis explicitly", () => {
      expect(format_days_old_badge(311, "check_date")).toBe("311 days (check)")
      expect(format_days_old_detail(807, "invoice_date")).toBe("807 days (since invoice date)")
      expect(format_days_old_detail(10, "check_date")).toBe("10 days (since check date)")
    })

    it("formats days remaining for the dispute-expiry basis", () => {
      expect(format_days_old_badge(53, "until_dispute_expiration")).toBe("53 days left (expiry)")
      expect(format_days_old_badge(-5, "until_dispute_expiration")).toBe("Expired")
      expect(format_days_old_detail(53, "until_dispute_expiration")).toBe("53 days left (until dispute expires)")
      expect(format_days_old_detail(-5, "until_dispute_expiration")).toBe("Expired 5 days ago")
      expect(days_old_column_title("until_dispute_expiration")).toBe("Days Left (expiry)")
    })
  })

  describe("compute_age_days", () => {
    it("computes invoice date age", () => {
      expect(compute_age_days(row({ invoice_date: "2026-06-15" }), "invoice_date", NOW)).toBe(30)
    })

    it("check date basis uses check_date and falls back to invoice_date", () => {
      expect(compute_age_days(row({ invoice_date: "2026-01-01", check_date: "2026-07-05" }), "check_date", NOW)).toBe(10)
      expect(compute_age_days(row({ invoice_date: "2026-07-05", check_date: undefined }), "check_date", NOW)).toBe(10)
    })

    it("since_disputed uses dispute_created_at and is null without one", () => {
      expect(compute_age_days(row({ dispute_created_at: "2026-07-10T08:00:00Z" }), "since_disputed", NOW)).toBe(5)
      expect(compute_age_days(row({}), "since_disputed", NOW)).toBeNull()
    })

    it("in_current_stage uses status_changed_at and is null without one", () => {
      expect(compute_age_days(row({ status_changed_at: "2026-06-15T00:00:00Z" }), "in_current_stage", NOW)).toBe(30)
      expect(compute_age_days(row({}), "in_current_stage", NOW)).toBeNull()
    })

    it("until_dispute_expiration returns days remaining, negative when expired", () => {
      expect(compute_age_days(row({ dispute_deadline: "2026-08-14" }), "until_dispute_expiration", NOW)).toBe(29)
      // differenceInDays truncates toward zero: 14.5 days past the deadline reports -14
      expect(compute_age_days(row({ dispute_deadline: "2026-07-01" }), "until_dispute_expiration", NOW)).toBe(-14)
      expect(compute_age_days(row({}), "until_dispute_expiration", NOW)).toBeNull()
    })
  })

  describe("bucket_for_days", () => {
    it("assigns age bucket edges correctly", () => {
      expect(bucket_for_days(0, "invoice_date")!.key).toBe("0_30")
      expect(bucket_for_days(30, "invoice_date")!.key).toBe("0_30")
      expect(bucket_for_days(31, "invoice_date")!.key).toBe("31_60")
      expect(bucket_for_days(60, "invoice_date")!.key).toBe("31_60")
      expect(bucket_for_days(61, "invoice_date")!.key).toBe("61_90")
      expect(bucket_for_days(90, "invoice_date")!.key).toBe("61_90")
      expect(bucket_for_days(91, "invoice_date")!.key).toBe("90_plus")
      expect(bucket_for_days(1000, "invoice_date")!.key).toBe("90_plus")
    })

    it("negative age falls outside all age buckets", () => {
      expect(bucket_for_days(-1, "invoice_date")).toBeNull()
    })

    it("assigns expiry buckets, including expired for negative days", () => {
      expect(bucket_for_days(-1, "until_dispute_expiration")!.key).toBe("expired")
      expect(bucket_for_days(0, "until_dispute_expiration")!.key).toBe("expiring_30")
      expect(bucket_for_days(30, "until_dispute_expiration")!.key).toBe("expiring_30")
      expect(bucket_for_days(31, "until_dispute_expiration")!.key).toBe("expiring_31_90")
      expect(bucket_for_days(91, "until_dispute_expiration")!.key).toBe("expiring_90_plus")
    })
  })

  describe("age_bucket_filter", () => {
    it("matches rows whose age falls in the range", () => {
      const value: AgeBucketFilterValue = { basis: "invoice_date", min: 31, max: 60 }
      expect(age_bucket_filter(row({ invoice_date: "2026-06-01" }), value, NOW)).toBe(true) // 44 days
      expect(age_bucket_filter(row({ invoice_date: "2026-07-01" }), value, NOW)).toBe(false) // 14 days
    })

    it("excludes rows with no age for the basis", () => {
      const value: AgeBucketFilterValue = { basis: "since_disputed", min: 0, max: null }
      expect(age_bucket_filter(row({}), value, NOW)).toBe(false)
    })

    it("guard identifies filter values vs plain text", () => {
      expect(is_age_bucket_filter_value({ basis: "invoice_date", min: 0, max: 30 })).toBe(true)
      expect(is_age_bucket_filter_value("45")).toBe(false)
      expect(is_age_bucket_filter_value(null)).toBe(false)
    })
  })

  describe("bucket emphasis", () => {
    it("only the expiring-soon bucket is emphasized", () => {
      expect(AGE_BUCKETS.every(b => !b.emphasized)).toBe(true)
      expect(EXPIRY_BUCKETS.filter(b => b.emphasized).map(b => b.key)).toEqual(["expiring_30"])
    })
  })

  describe("stage presets", () => {
    it("open stages exclude terminal stages", () => {
      for (const terminal of ["won", "lost", "archived", "expensable", "validated"]) {
        expect(OPEN_STAGES).not.toContain(terminal)
      }
      expect(OPEN_STAGES).toContain("disputed")
    })

    it("presets map to expected stages", () => {
      expect(STAGE_PRESETS.open_pending_validation.stages).toEqual(["new", "review", "pending", "task_assigned"])
      expect(STAGE_PRESETS.open_pending_backup.stages).toEqual(["backup_requested"])
      expect(STAGE_PRESETS.open_pending_dispute.stages).toEqual(["disputable", "backup_received"])
      expect(STAGE_PRESETS.all.stages).toBeNull()
    })

    it("stage_in_scope handles null scope", () => {
      expect(stage_in_scope("won", null)).toBe(true)
      expect(stage_in_scope("won", OPEN_STAGES)).toBe(false)
      expect(stage_in_scope("new", OPEN_STAGES)).toBe(true)
    })

    it("ALL_STAGES covers every labeled stage exactly once", async () => {
      const { DEDUCTION_STATUS_LABELS } = await import("@/utils/deduction_status_labels")
      expect([...ALL_STAGES].sort()).toEqual(Object.keys(DEDUCTION_STATUS_LABELS).sort())
      expect(new Set(ALL_STAGES).size).toBe(ALL_STAGES.length)
    })
  })

  describe("aging_row_amount", () => {
    it("mirrors tallyData selection rules", () => {
      expect(aging_row_amount(row({ status_value: "won", won_amount: 55 }))).toBe(55)
      expect(aging_row_amount(row({ kind: "invoice", deduction_amount: 100, dispute_amount: 5000 }))).toBe(100)
      expect(aging_row_amount(row({ dispute_amount: 250, deduction_amount: 100 }))).toBe(250)
      expect(aging_row_amount(row({ deduction_amount: 100 }))).toBe(100)
    })
  })

  describe("aging_summary", () => {
    const data = [
      row({ invoice_date: "2026-07-10", deduction_amount: 10, customer_name: "KeHE" }), // 5 days
      row({ invoice_date: "2026-06-01", deduction_amount: 20, customer_name: "KeHE" }), // 44 days
      row({ invoice_date: "2026-01-01", deduction_amount: 40, customer_name: "UNFI" }), // 195 days
      row({ invoice_date: "2026-01-01", deduction_amount: 80, customer_name: "UNFI", status_value: "won" }), // out of scope
      row({ invoice_date: "2026-01-01", deduction_amount: 160, customer_name: "UNFI", kind: "repayment" }), // skipped
    ]

    it("tallies buckets and totals for open rows only", () => {
      const s = aging_summary(data, "invoice_date", OPEN_STAGES, NOW)
      expect(s.total_count).toBe(3)
      expect(s.total_amount).toBe(70)
      expect(s.buckets["0_30"]).toEqual({ count: 1, amount: 10 })
      expect(s.buckets["31_60"]).toEqual({ count: 1, amount: 20 })
      expect(s.buckets["61_90"]).toEqual({ count: 0, amount: 0 })
      expect(s.buckets["90_plus"]).toEqual({ count: 1, amount: 40 })
    })

    it("rolls up per customer sorted by amount desc", () => {
      const s = aging_summary(data, "invoice_date", OPEN_STAGES, NOW)
      expect(s.by_customer.map(c => c.customer_name)).toEqual(["UNFI", "KeHE"])
      expect(s.by_customer[0].buckets["90_plus"].amount).toBe(40)
      expect(s.by_customer[1].total_amount).toBe(30)
    })

    it("null scope includes terminal stages", () => {
      const s = aging_summary(data, "invoice_date", null, NOW)
      expect(s.total_count).toBe(4)
    })

    it("skips rows without an age for the basis", () => {
      const s = aging_summary(data, "since_disputed", null, NOW)
      expect(s.total_count).toBe(0)
    })
  })

  describe("limit_customers", () => {
    it("rolls up remainder into Other", () => {
      const s = aging_summary(
        [
          row({ invoice_date: "2026-07-10", deduction_amount: 10, customer_name: "A" }),
          row({ invoice_date: "2026-07-10", deduction_amount: 20, customer_name: "B" }),
          row({ invoice_date: "2026-07-10", deduction_amount: 30, customer_name: "C" }),
        ],
        "invoice_date",
        null,
        NOW
      )
      const limited = limit_customers(s.by_customer, 2, "invoice_date")
      expect(limited.map(c => c.customer_name)).toEqual(["C", "B", "Other"])
      expect(limited[2].total_amount).toBe(10)
      expect(limited[2].buckets["0_30"].count).toBe(1)
    })

    it("returns rows unchanged when under the limit", () => {
      expect(limit_customers([], 5, "invoice_date")).toEqual([])
    })
  })
}
